import { useState } from 'react';
import { X, Monitor, PenTool, Loader2, Siren } from 'lucide-react';
import { CardSelectInput } from '@/Components/shared/CardSelectInput';
import { FormInput } from '@/Components/shared/FormInput';
import { PhoneInput } from '@/Components/shared/PhoneInput';

interface ContractConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (signatureType: 1 | 2, email?: string, phone?: string) => void;
  defaultEmail: string;
  defaultPhone: string;
  defaultSignatureType: 1 | 2;
  isSubmitting: boolean;
  warrantyType?: number;
  guarantorName?: string;
  /**
   * Libellé de la formule d'assistance portée par la garantie, s'il y en a une.
   *
   * La formule n'est PAS choisie ici : elle vient du paramétrage de la garantie et
   * sera figée sur le contrat. On la rappelle simplement au vendeur avant qu'il
   * valide, pour qu'il sache ce qu'il vend.
   */
  assistanceSentence?: string | null;
}

/**
 * Modale de confirmation de création de contrat (sans paiement Stripe).
 * Permet de choisir le type de signature et de vérifier/modifier les coordonnées
 * du bénéficiaire si la signature électronique est sélectionnée.
 */
export function ContractConfirmationModal({
  isOpen,
  onClose,
  onConfirm,
  defaultEmail,
  defaultPhone,
  defaultSignatureType,
  isSubmitting,
  warrantyType,
  guarantorName,
  assistanceSentence,
}: ContractConfirmationModalProps) {
  const [signatureType, setSignatureType] = useState<1 | 2>(defaultSignatureType);
  const [email, setEmail] = useState(defaultEmail);
  const [phone, setPhone] = useState(defaultPhone);

  if (!isOpen) return null;

  const isElectronic = signatureType === 1;
  const canConfirm = !isElectronic || (!!email && email.includes('@'));

  const handleConfirm = () => {
    onConfirm(signatureType, isElectronic ? email : undefined, isElectronic ? phone : undefined);
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  return (
    <div className="tw-fixed tw-inset-0 tw-bg-black tw-bg-opacity-50 tw-flex tw-items-center tw-justify-center tw-z-[101] tw-p-4">
      <div className="tw-bg-white tw-rounded-lg tw-shadow-xl tw-max-w-2xl tw-w-full tw-max-h-[80vh] tw-overflow-y-auto">
        {/* Header */}
        <div className="tw-flex tw-items-center tw-justify-between tw-p-6 tw-border-b tw-border-gray-200">
          <div className="tw-flex tw-items-center tw-gap-3">
            <div className="tw-w-10 tw-h-10 tw-bg-affinicar-lighter tw-rounded-full tw-flex tw-items-center tw-justify-center">
              <PenTool className="tw-w-5 tw-h-5 tw-text-affinicar-primary" />
            </div>
            <h2 className="tw-text-xl tw-font-bold tw-text-gray-900">
              Confirmation du contrat
            </h2>
          </div>
          <button
            onClick={handleClose}
            disabled={isSubmitting}
            className="tw-p-2 tw-text-gray-400 hover:tw-text-gray-600 tw-transition-colors disabled:tw-opacity-50"
          >
            <X className="tw-w-5 tw-h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="tw-p-6 tw-space-y-6">
          {/* Rappel de l'assistance adossée à la garantie. Purement informatif :
              rien à choisir, la formule vient de la garantie. */}
          {assistanceSentence && (
            <div className="tw-flex tw-items-start tw-gap-3 tw-rounded-lg tw-border tw-border-affinicar-primary/30 tw-bg-affinicar-lighter tw-p-4">
              <Siren className="tw-w-5 tw-h-5 tw-text-affinicar-primary tw-flex-shrink-0 tw-mt-0.5" />
              <div className="tw-text-sm tw-text-gray-700">
                {/* Même phrase que la bannière de la fiche contrat : le vendeur lit
                    au moment de vendre exactement ce que le client verra ensuite. */}
                <p className="tw-font-semibold tw-text-gray-900 tw-mb-1">
                  {assistanceSentence}
                </p>
                <p className="tw-mb-0 tw-text-xs">
                  Une notice d'assistance sera transmise au bénéficiaire par mail, en
                  complément du contrat de garantie.
                </p>
              </div>
            </div>
          )}
          {/* Signature type selector */}
          <div>
            <h4 className="tw-text-sm tw-font-semibold tw-text-gray-900 tw-mb-3">Type de signature</h4>
            <CardSelectInput
              options={[
                { value: '1', label: 'Électronique', description: 'Signature en ligne par le client', icon: <Monitor className="tw-w-5 tw-h-5" /> },
                { value: '2', label: 'Manuscrite', description: 'Signature papier physique', icon: <PenTool className="tw-w-5 tw-h-5" /> },
              ]}
              value={signatureType.toString()}
              onChange={(val) => setSignatureType(parseInt(val, 10) as 1 | 2)}
              name="signature-type"
              required
              columns={2}
            />
          </div>

          {/* Email/phone for electronic signature */}
          {isElectronic && (
            <div className="tw-space-y-4">
              <p className="tw-text-sm tw-text-gray-600">
                La signature électronique sera envoyée aux coordonnées ci-dessous. Vous pouvez les modifier si nécessaire.
              </p>

              <FormInput
                type="email"
                label="Email du bénéficiaire"
                value={email}
                onChange={setEmail}
                placeholder="email@exemple.fr"
                required
              />

              <PhoneInput
                label="Téléphone du bénéficiaire"
                value={phone}
                onChange={setPhone}
                required
              />
            </div>
          )}
        </div>

        {/* Engagement légal */}
        {warrantyType !== undefined && (
          <div className="tw-px-6 tw-pb-2">
            <p className="tw-text-xs tw-text-gray-500 tw-italic tw-mb-0">
              {guarantorName ? (
                warrantyType === 1
                  ? <>En cliquant sur "Générer le contrat", j'accepte les termes du contrat et engage la société <strong>{guarantorName}</strong> à prendre en charge les prestations de maintenance corrective.</>
                  : <>En cliquant sur "Générer le contrat", j'accepte les termes du contrat et engage la société <strong>{guarantorName}</strong> en tant que garant.</>
              ) : (
                warrantyType === 1
                  ? 'En cliquant sur "Générer le contrat", j\'accepte les termes du contrat et engage ma société à prendre en charge les prestations de maintenance corrective.'
                  : 'En cliquant sur "Générer le contrat", j\'accepte les termes du contrat et engage ma société en tant que garant.'
              )}
            </p>
          </div>
        )}

        {/* Footer */}
        <div className="tw-flex tw-justify-end tw-gap-3 tw-p-6 tw-border-t tw-border-gray-200">
          <button
            type="button"
            onClick={handleClose}
            disabled={isSubmitting}
            className="tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-gray-700 tw-bg-white tw-border tw-border-gray-300 tw-rounded-lg hover:tw-bg-gray-50 tw-transition-colors disabled:tw-opacity-50"
          >
            Annuler
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={isSubmitting || !canConfirm}
            className="tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-white tw-bg-affinicar-primary tw-rounded-lg hover:tw-bg-affinicar-secondary tw-transition-colors disabled:tw-opacity-50 disabled:tw-cursor-not-allowed tw-flex tw-items-center tw-gap-2"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="tw-w-4 tw-h-4 tw-animate-spin" />
                Création en cours...
              </>
            ) : (
              'Confirmer et créer le contrat'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
